/** Лента запусков героини: новые сверху; строка = задача (модель), ячейка = картинка или ожидание/ошибка. */
import { Link } from "react-router";

import { ImageCard } from "@/components/persona/ImageCard";
import { Empty, Hint, Panel } from "@/components/project/ui";
import { when } from "@/lib/media";
import { modelLabel, type Job, type PersonaView, type Run } from "@/lib/persona";
import { usd } from "@/lib/project";
import { runErrorText } from "@/lib/runError";

const KIND_LABEL: Record<Run["kind"], string> = {
  portrait: "Портрет",
  sheet: "Лист персонажа",
  iterate: "Итерация",
  rebuild: "Пересборка из канона",
};

function Waiting({ job }: { job: Job }) {
  return (
    <div className="grid aspect-[3/4] animate-pulse place-items-center rounded-[var(--radius-ui)] border border-border bg-bg-hover">
      <span className="text-[11.5px] text-fg-faint tnum">
        {job.status === "running" ? "генерируется" : "в очереди"}
        {job.cost_estimate !== null ? ` · ~${usd(job.cost_estimate / Math.max(job.n, 1))}` : ""}
      </span>
    </div>
  );
}

/** Что с деньгами у упавшей задачи. «Не списаны» — только при ответе-отказе провайдера без цены:
 * обрыв без ответа, 200 без картинки и сбой после оплаты могли стоить денег — отправляем в журнал. */
function Money({ job }: { job: Job }) {
  const journal = (word: string) => (
    <Link to="/calls" className="underline underline-offset-2">
      {word}
    </Link>
  );
  if (job.cost_actual !== null) {
    return (
      <>
        оплачено {usd(job.cost_actual)} — результат в {journal("журнале")}
      </>
    );
  }
  if (job.http_status !== null && job.http_status !== 200) return <>деньги не списаны</>;
  return <>возможно оплачено — проверьте {journal("журнал")}</>;
}

function Failed({ job }: { job: Job }) {
  const text = runErrorText(job.http_status, job.error);
  return (
    <div className="grid min-h-24 content-center gap-1 rounded-[var(--radius-ui)] border border-err/40 bg-err-bg p-2.5 text-[12px] text-err">
      <span>{text}</span>
      <span className="text-[11.5px] opacity-80">
        <Money job={job} />
      </span>
    </div>
  );
}

function JobRow({
  job,
  models,
  openImage,
  onOpen,
}: {
  job: Job;
  models: PersonaView["models"];
  openImage: number | null;
  onOpen: (id: number) => void;
}) {
  const pending = (job.status === "queued" || job.status === "running") && job.images.length === 0;
  return (
    <div className="grid gap-1.5">
      <Hint>{modelLabel(models, job.model)}</Hint>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2">
        {job.status === "failed" ? (
          <Failed job={job} />
        ) : pending ? (
          Array.from({ length: job.n }, (_, i) => <Waiting key={i} job={job} />)
        ) : (
          job.images.map((img) => (
            <ImageCard key={img.id} image={img} seconds={job.seconds} open={openImage === img.id} onOpen={onOpen} />
          ))
        )}
      </div>
    </div>
  );
}

export function RunsFeed({
  view,
  openImage,
  onOpen,
}: {
  view: PersonaView;
  openImage: number | null;
  onOpen: (id: number) => void;
}) {
  // Сервер отдаёт по возрастанию id или нет — не гадаем: новые первыми по номеру запуска.
  const runs = [...view.runs].sort((a, b) => b.id - a.id);
  return (
    <Panel title="Запуски" aside={`потрачено ${usd(view.spent, 2)}`} annot="лента запусков">
      {runs.length === 0 ? (
        <Empty>Запусков ещё нет.</Empty>
      ) : (
        <div className="grid gap-5">
          {runs.map((run) => {
            const fact = run.jobs.reduce((sum, j) => sum + (j.cost_actual ?? 0), 0);
            return (
              <section key={run.id} id={`run-${run.id}`} className="grid scroll-mt-20 gap-2">
                <h3 className="text-[12.5px] font-medium text-fg tnum">
                  {KIND_LABEL[run.kind]} · v{run.prompt_version} · {run.author_name} · {when(run.date_create)} · оценка{" "}
                  {usd(run.estimate)} → факт {usd(fact)}
                </h3>
                {run.jobs.map((job) => (
                  <JobRow key={job.id} job={job} models={view.models} openImage={openImage} onOpen={onOpen} />
                ))}
              </section>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
